import { useEffect } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'
import { RouteParams, routes } from '@sketch/modules-common'
import { useShowSingleEntity } from '@sketch-hq/sketch-web-renderer'

type PreviewEntityParams = Partial<
  RouteParams<'PREVIEW_FRAME_VIEW'> & RouteParams<'PREVIEW_PAGE_SECTION'>
>

/**
 * Resolves which single entity - if any - the current Preview route focuses on,
 * and tells the renderer about it.
 *
 * Either kind of focused entity is isolated by becoming the renderer's scene
 * root (through `rootEntityUUID`), mirroring the Share counterparts of these
 * views (FrameView and SectionView). A focused frame additionally goes through
 * the renderer's single-entity state, which hides the rest of the page and fits
 * the camera to the frame, animating the move between the two views.
 *
 * Entity metadata (name, bounds) is available from `usePRFileArtboards()` /
 * `usePRFileSections()` once the canvas is loaded — no extra GQL query needed.
 */
export function usePreviewSingleEntity() {
  const frameMatch = useRouteMatch({
    path: routes.PREVIEW_FRAME_VIEW.template(),
    exact: true,
  })
  const sectionMatch = useRouteMatch({
    path: routes.PREVIEW_PAGE_SECTION.template(),
    exact: true,
  })

  const { frameUUID, sectionUUID } = useParams<PreviewEntityParams>()

  const focusedFrameUUID = frameMatch ? frameUUID : undefined
  const focusedSectionUUID = sectionMatch ? sectionUUID : undefined

  const [, setShowSingleEntity] = useShowSingleEntity()

  // Only the frame view hides the rest of the page this way - being the scene
  // root is enough to isolate a Section, and keeps its children navigable. It
  // also fits the camera to the frame, which is what moves the view when
  // navigating between entities without remounting the canvas: the stored
  // camera position is only read at init (see useInitialCameraPosition).
  useEffect(() => {
    setShowSingleEntity(focusedFrameUUID || null)
  }, [focusedFrameUUID, setShowSingleEntity])

  return {
    isShowingSingleFrame: !!frameMatch,
    frameUUID: focusedFrameUUID,
    isShowingSingleSection: !!sectionMatch,
    sectionUUID: focusedSectionUUID,
    /** The focused entity, whichever kind it is. Undefined in page view. */
    entityUUID: focusedSectionUUID ?? focusedFrameUUID,
  }
}
